import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Download } from 'lucide-react';
import { toast } from 'sonner';
import { heroData } from '../data/mock';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToQuoteForm = () => {
    const element = document.getElementById('quote-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleDownload = () => {
    // Download the company brochure
    const link = document.createElement('a');
    link.href = '/A-To-Z-Infraspares-Brochure.pdf';
    link.download = 'A-To-Z-Infraspares-Brochure.pdf';
    link.click();
    
    toast.success('Download Started!', {
      description: 'Your brochure is downloading...',
      duration: 3000,
    });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${heroData.heroImage})`,
            backgroundPosition: 'center center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-green-900/70" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-900/50" />
      </div>

      {/* Animated Grid Pattern */}
      <div className="absolute inset-0 z-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 lg:px-8 py-32 relative z-10">
        <div className="max-w-4xl">
          {/* Badge */}
          <div className={`inline-flex items-center space-x-2 bg-green-500/20 backdrop-blur-sm border border-green-400/30 rounded-full px-4 py-2 mb-8 transform transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-green-300 text-sm font-medium">India's Premier Spare Parts Partner</span>
          </div>

          {/* Main Headline */}
          <h1 className={`text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight transform transition-all duration-700 delay-100 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            {heroData.headline}
          </h1>

          {/* Subheadline */}
          <h2 className={`text-2xl md:text-3xl lg:text-4xl font-semibold text-green-400 mb-6 transform transition-all duration-700 delay-200 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            {heroData.subheadline}
          </h2>

          {/* Description */}
          <p className={`text-lg md:text-xl text-gray-300 mb-10 leading-relaxed max-w-3xl transform transition-all duration-700 delay-300 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            {heroData.description}
          </p>

          {/* CTA Buttons */}
          <div className={`flex flex-col sm:flex-row gap-4 transform transition-all duration-700 delay-400 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <Button 
              size="lg"
              onClick={scrollToQuoteForm}
              className="bg-green-500 hover:bg-green-500 text-white px-8 py-6 text-lg font-semibold shadow-2xl hover:shadow-green-400/50 transition-all group"
            >
              {heroData.ctaText}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={handleDownload}
              className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg font-semibold transition-all group"
            >
              <Download className="mr-2 w-5 h-5 group-hover:scale-110 transition-transform" />
              {heroData.secondaryCtaText}
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className={`flex flex-wrap gap-8 mt-16 transform transition-all duration-700 delay-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-500/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-green-500/30">
                <span className="text-green-400 font-bold text-xl">8</span>
              </div>
              <div>
                <p className="text-white font-semibold">Strategic Locations</p>
                <p className="text-gray-400 text-sm">Nationwide Coverage</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-500/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-green-500/30">
                <span className="text-green-400 font-bold text-xl">50+</span>
              </div>
              <div>
                <p className="text-white font-semibold">Expert Team</p>
                <p className="text-gray-400 text-sm">Industry Specialists</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="flex flex-col items-center animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
